import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  CheckCircle,
  Loader2,
  Radio,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import {
  useCommunicateStatus,
  useProvisionCommunicate,
  useUpdateCommunicateConfig,
} from '@/hooks/useClinicCommunicateSettings';
import { ApiCallError } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Input } from '@/components/ui/Input';
import { IntegrationsBackLink } from '@/pages/settings/ClinicIntegrations';

export function ClinicCommunicate() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { isClinicAdmin, isPlatformAdmin } = useAuth();

  const { data: status, isLoading, error, isError, isFetching, refetch } =
    useCommunicateStatus(activeClinicId);
  const provision = useProvisionCommunicate(activeClinicId);
  const updateConfig = useUpdateCommunicateConfig(activeClinicId);

  const canAdmin = Boolean(
    activeClinicId && (isPlatformAdmin || isClinicAdmin(activeClinicId ?? '')),
  );

  const [sorId, setSorId] = useState('');
  const [ean, setEan] = useState('');

  useEffect(() => {
    if (!status?.connected) {
      setSorId('');
      setEan('');
      return;
    }
    setSorId(status.sorId ?? '');
    setEan(status.ean ?? '');
  }, [status?.connected, status?.sorId, status?.ean]);

  async function handleConnect() {
    if (!activeClinicId) return;
    try {
      const result = await provision.mutateAsync();
      toast.success(
        result.alreadyProvisioned
          ? t('settings.communicate.already_connected')
          : t('settings.communicate.connect_success'),
      );
    } catch (err) {
      console.error('communicate provision failed', err);
      const message =
        err instanceof ApiCallError && err.message
          ? err.message
          : t('settings.communicate.connect_error');
      toast.error(message);
    }
  }

  async function handleSaveConfig() {
    if (!activeClinicId) return;
    try {
      await updateConfig.mutateAsync({
        sorId: sorId.trim() || null,
        ean: ean.trim() || null,
      });
      toast.success(t('settings.communicate.config_saved'));
    } catch (err) {
      console.error('communicate config update failed', err);
      const message =
        err instanceof ApiCallError && err.message
          ? err.message
          : t('settings.communicate.config_error');
      toast.error(message);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error && !status) {
    return <ErrorAlert message={t('settings.communicate.status_error')} />;
  }

  const connected = Boolean(status?.connected);
  const connectionStatus = status?.status ?? null;
  const healthOk = !isError;

  return (
    <div className="space-y-6">
      <IntegrationsBackLink />

      <div>
        <div className="flex items-center gap-2">
          <Radio className="h-5 w-5 text-indigo-500" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('settings.integrations.cards.communicate_title')}
          </h2>
        </div>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('settings.communicate.subtitle')}
        </p>
      </div>

      {/* Health (status query success/error — lightweight) */}
      {!healthOk && (
        <div className="flex items-center gap-2 rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 dark:border-yellow-700 dark:bg-yellow-900/30">
          <AlertTriangle className="h-4 w-4 shrink-0 text-yellow-600 dark:text-yellow-400" />
          <span className="text-sm text-yellow-800 dark:text-yellow-300">
            {t('settings.communicate.health_unavailable')}
          </span>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="ml-auto"
            onClick={() => void refetch()}
            disabled={isFetching}
          >
            {isFetching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              t('settings.communicate.retry')
            )}
          </Button>
        </div>
      )}

      {/* Connection status card */}
      <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-card-dark">
        <div className="mb-4 flex items-center gap-3">
          <Radio className="h-5 w-5 text-indigo-500" />
          <h3 className="text-base font-medium text-gray-900 dark:text-white">
            {t('settings.communicate.connection_title')}
          </h3>
          <span
            className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
              connected
                ? statusBadgeClass(connectionStatus)
                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
            }`}
          >
            {connected
              ? t(`settings.communicate.status_${connectionStatus ?? 'pending'}`)
              : t('settings.communicate.status_not_connected')}
          </span>
        </div>

        {!connected && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('settings.communicate.not_connected_desc')}
            </p>
            {canAdmin && (
              <Button onClick={handleConnect} disabled={provision.isPending}>
                {provision.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Radio className="mr-2 h-4 w-4" />
                )}
                {t('settings.communicate.connect')}
              </Button>
            )}
          </div>
        )}

        {connected && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
              <CheckCircle className="h-5 w-5" />
              <span className="text-sm font-medium">
                {t('settings.communicate.connected_label')}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500 dark:text-gray-400">
                  {t('settings.communicate.field_status')}:{' '}
                </span>
                <span className="text-gray-900 dark:text-white">
                  {t(`settings.communicate.status_${connectionStatus ?? 'pending'}`)}
                </span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">
                  {t('settings.communicate.field_webhook')}:{' '}
                </span>
                <span
                  className={
                    status?.hasWebhookSecret
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-gray-500'
                  }
                >
                  {status?.hasWebhookSecret ? t('common.yes') : t('common.no')}
                </span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">
                  {t('settings.communicate.field_sor')}:{' '}
                </span>
                <span className="font-mono text-gray-900 dark:text-white">
                  {status?.sorId ?? '—'}
                </span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">
                  {t('settings.communicate.field_ean')}:{' '}
                </span>
                <span className="font-mono text-gray-900 dark:text-white">
                  {status?.ean ?? '—'}
                </span>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* SOR / EAN configuration */}
      {connected && canAdmin && (
        <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-card-dark">
          <h3 className="mb-2 text-base font-medium text-gray-900 dark:text-white">
            {t('settings.communicate.config_title')}
          </h3>
          <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
            {t('settings.communicate.config_desc')}
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              name="communicate-sor"
              label={t('settings.communicate.field_sor')}
              value={sorId}
              onChange={(e) => setSorId(e.target.value)}
              placeholder={t('settings.communicate.field_sor_placeholder')}
              autoComplete="off"
            />
            <Input
              name="communicate-ean"
              label={t('settings.communicate.field_ean')}
              value={ean}
              onChange={(e) => setEan(e.target.value)}
              placeholder={t('settings.communicate.field_ean_placeholder')}
              autoComplete="off"
            />
          </div>
          <div className="mt-4">
            <Button onClick={handleSaveConfig} disabled={updateConfig.isPending}>
              {updateConfig.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              {t('settings.communicate.save_config')}
            </Button>
          </div>
        </section>
      )}
    </div>
  );
}

function statusBadgeClass(status: string | null): string {
  switch (status) {
    case 'active':
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200';
    case 'pending':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    case 'disabled':
      return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200';
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200';
  }
}
